import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixMultilingualSettingsV2SettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/settings',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/settings',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/settings',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/settings',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/settings',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/locale-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_multilingual_locale-settings';

/** Retrieves a site's locale settings. */
export function getLocaleSettings(payload: object): RequestOptionsFactory<any> {
  function __getLocaleSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.settings.v2.locale_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.multilingual.settings.v2.SettingsService.GetLocaleSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualSettingsV2SettingsServiceUrl({
        protoPath: '/v2/settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localeSettings.createdDate' },
              { path: 'localeSettings.updatedDate' },
              { path: 'localeSettings.primaryLocale.createdDate' },
              { path: 'localeSettings.primaryLocale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLocaleSettings;
}

/**
 * Updates a site's locale settings.
 *
 * > **Note:**
 * > You can't call this method to update a site's multilingual mode. To enable or disable multilingual mode, call Set Multilingual Mode.
 */
export function updateLocaleSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateLocaleSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'localeSettings.createdDate' },
          { path: 'localeSettings.updatedDate' },
          { path: 'localeSettings.primaryLocale.createdDate' },
          { path: 'localeSettings.primaryLocale.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.settings.v2.locale_settings',
      method: 'PATCH' as any,
      methodFqn:
        'wix.multilingual.settings.v2.SettingsService.UpdateLocaleSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualSettingsV2SettingsServiceUrl({
        protoPath: '/v2/settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localeSettings.createdDate' },
              { path: 'localeSettings.updatedDate' },
              { path: 'localeSettings.primaryLocale.createdDate' },
              { path: 'localeSettings.primaryLocale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLocaleSettings;
}

/**
 * Sets a site's multilingual mode.
 *
 * Multilingual mode allows a site to support multiple locales. This means that when multilingual mode is enabled, you can create and manage secondary locales.
 *
 * To disable multilingual mode, all secondary locales must first be removed. This action removes all translated content from your site. Disabling multilingual mode will also reset the locale settings to their default values.
 *
 * <blockquote class="important">
 * <strong>Important:</strong>
 *
 * You can only call this method on sites where a Wix user has installed the [Wix Multilingual app](https://www.wix.com/app-market/wix-multilingual?searchLocation=home).
 *
 * </blockquote>
 */
export function setMultilingualMode(
  payload: object
): RequestOptionsFactory<any> {
  function __setMultilingualMode({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.settings.v2.locale_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.settings.v2.SettingsService.SetMultilingualMode',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualSettingsV2SettingsServiceUrl({
        protoPath: '/v2/settings/mode',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localeSettings.createdDate' },
              { path: 'localeSettings.updatedDate' },
              { path: 'localeSettings.primaryLocale.createdDate' },
              { path: 'localeSettings.primaryLocale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setMultilingualMode;
}
